import React from 'react';
import { Award, Clock, Heart, Settings } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useCmsList } from '../../hooks/useCms';
import { FALLBACK_VALUES, valueT } from '../../data/cmsFallbacks';

const iconMap = { award: Award, clock: Clock, heart: Heart, settings: Settings };

const BenefitsSection = () => {
  const { t } = useLanguage();

  const benefits = useCmsList('/api/cms/values', FALLBACK_VALUES);

  return (
    <section className="relative py-32 bg-[#F9F9F9]">
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="h-px w-12 bg-[#D4AF37]" />
            <span className="text-black/50 text-sm font-medium uppercase tracking-[0.3em]">
              {t('advantages', { de: 'Vorteile', en: 'Benefits' })}
            </span>
            <div className="h-px w-12 bg-[#D4AF37]" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#003366] mb-6">
            {t('whyChooseUs', { de: 'Warum uns wählen?', en: 'Why Choose Us?' })}
          </h2>
        </div>

        {/* Benefits Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => {
            const Icon = iconMap[benefit.icon];
            return (
              <div key={benefit.id} className="group relative">
                <div className="relative p-6 rounded-2xl bg-white transition-all duration-500 hover:-translate-y-2 hover:shadow-xl hover:shadow-black/5 overflow-hidden h-full">
                  
                  {/* Top-left corner frame with animated golden glow */}
                  <div className="absolute top-0 left-0 w-1/2 h-1/2 overflow-hidden rounded-tl-2xl pointer-events-none">
                    <div className="absolute top-0 left-0 w-[2px] h-full bg-black">
                      <div 
                        className="absolute left-0 w-full h-[30px]"
                        style={{
                          background: 'linear-gradient(180deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
                          animation: 'benefitGlowV 3s ease-in-out infinite',
                          boxShadow: '0 0 10px #D4AF37, 0 0 20px #D4AF37',
                        }}
                      />
                    </div>
                    <div className="absolute top-0 left-0 h-[2px] w-full bg-black">
                      <div 
                        className="absolute top-0 h-full w-[30px]"
                        style={{
                          background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
                          animation: 'benefitGlowH 3s ease-in-out infinite',
                          boxShadow: '0 0 10px #D4AF37, 0 0 20px #D4AF37',
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Bottom-right corner frame with animated golden glow */}
                  <div className="absolute bottom-0 right-0 w-1/2 h-1/2 overflow-hidden rounded-br-2xl pointer-events-none">
                    <div className="absolute bottom-0 right-0 w-[2px] h-full bg-black">
                      <div 
                        className="absolute right-0 w-full h-[30px]"
                        style={{
                          background: 'linear-gradient(0deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
                          animation: 'benefitGlowVR 3s ease-in-out infinite',
                          boxShadow: '0 0 10px #D4AF37, 0 0 20px #D4AF37',
                        }}
                      />
                    </div>
                    <div className="absolute bottom-0 right-0 h-[2px] w-full bg-black">
                      <div 
                        className="absolute bottom-0 h-full w-[30px]"
                        style={{
                          background: 'linear-gradient(270deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
                          animation: 'benefitGlowHR 3s ease-in-out infinite',
                          boxShadow: '0 0 10px #D4AF37, 0 0 20px #D4AF37',
                        }}
                      />
                    </div>
                  </div>

                  {/* Icon */}
                  <div className="relative w-14 h-14 rounded-xl bg-[#003366] flex items-center justify-center mb-5 mx-auto group-hover:bg-[#D4AF37] transition-all duration-500">
                    <Icon className="w-7 h-7 text-[#D4AF37] group-hover:text-[#003366] transition-colors duration-500" />
                  </div>
                  
                  {/* Title */}
                  <h3 className="relative text-lg font-bold text-[#003366] mb-2 text-center">
                    {t('title', valueT(benefit))}
                  </h3>
                  
                  {/* Description */}
                  <p className="relative text-black/60 text-sm leading-relaxed text-center">
                    {t('description', valueT(benefit))}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Benefits Corner Glow Animations */}
      <style>{`
        @keyframes benefitGlowV { 0% { top: -30px; opacity: 0; } 15% { opacity: 1; } 85% { opacity: 1; } 100% { top: 100%; opacity: 0; } }
        @keyframes benefitGlowH { 0% { left: -30px; opacity: 0; } 15% { opacity: 1; } 85% { opacity: 1; } 100% { left: 100%; opacity: 0; } }
        @keyframes benefitGlowVR { 0% { bottom: -30px; opacity: 0; } 15% { opacity: 1; } 85% { opacity: 1; } 100% { bottom: 100%; opacity: 0; } }
        @keyframes benefitGlowHR { 0% { right: -30px; opacity: 0; } 15% { opacity: 1; } 85% { opacity: 1; } 100% { right: 100%; opacity: 0; } }
      `}</style>
    </section>
  );
};

export default BenefitsSection;
